import { useState, useEffect } from 'react';
import { scopeService, type SavedScope } from '../../services/scopeService';
import type { Deliverable } from '@shared/types';
import { PlusIcon, TrashIcon, BookmarkIcon, PencilIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useConfirm } from '../../hooks/useConfirm';

export function ScopesManager() {
  const [scopes, setScopes] = useState<SavedScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedScope, setSelectedScope] = useState<SavedScope | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [scopeName, setScopeName] = useState('');
  const [scopeDescription, setScopeDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const { confirm, ConfirmDialog } = useConfirm();

  useEffect(() => {
    loadScopes();
  }, []);

  const loadScopes = async () => {
    setLoading(true);
    try {
      const data = await scopeService.getScopes();
      setScopes(data);
    } catch (error) {
      console.error('Failed to load scopes:', error);
      toast.error('Failed to load scopes');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (scope: SavedScope) => {
    setSelectedScope(scope);
    setIsEditing(true);
    setDeliverables(scope.deliverables);
    setScopeName(scope.name);
    setScopeDescription(scope.description || '');
  };

  const handleCancel = () => {
    setSelectedScope(null);
    setIsEditing(false);
    setDeliverables([]);
    setScopeName('');
    setScopeDescription('');
  };

  const handleSave = async () => {
    if (!scopeName.trim()) {
      toast.error('Please enter a scope name');
      return;
    }

    if (deliverables.length === 0) {
      toast.error('Please add at least one deliverable');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Saving scope...');

    try {
      if (selectedScope) {
        await scopeService.updateScope(selectedScope.id, {
          name: scopeName.trim(),
          description: scopeDescription.trim() || undefined,
          deliverables,
        });
        toast.success('Scope updated successfully!', { id: toastId });
      }

      handleCancel();
      loadScopes();
    } catch (error) {
      console.error('Failed to save scope:', error);
      toast.error('Failed to save scope', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scope: SavedScope) => {
    const confirmed = await confirm({
      title: 'Delete Scope',
      message: `Are you sure you want to delete "${scope.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });

    if (!confirmed) return;

    const toastId = toast.loading('Deleting scope...');
    try {
      await scopeService.deleteScope(scope.id);
      toast.success('Scope deleted successfully!', { id: toastId });
      
      if (selectedScope?.id === scope.id) {
        handleCancel();
      }
      
      loadScopes();
    } catch (error) {
      console.error('Failed to delete scope:', error);
      toast.error('Failed to delete scope', { id: toastId });
    }
  };

  const addDeliverable = () => {
    const newDeliverable: Deliverable = {
      id: crypto.randomUUID(),
      title: '',
      description: '',
    };
    setDeliverables([...deliverables, newDeliverable]);
  };

  const updateDeliverable = (id: string, field: keyof Deliverable, value: string) => {
    setDeliverables(deliverables.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const removeDeliverable = (id: string) => {
    setDeliverables(deliverables.filter((d) => d.id !== id));
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Scopes List */}
        <div>
          <div className="bg-white shadow-sm rounded-lg">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Saved Scopes</h2>
              <p className="text-sm text-gray-600 mt-1">Manage reusable deliverable templates</p>
            </div>

            <div className="p-4">
              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <p className="mt-2 text-sm text-gray-500">Loading scopes...</p>
                </div>
              ) : scopes.length === 0 ? (
                <div className="text-center py-8">
                  <BookmarkIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No saved scopes yet</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Create deliverables in contracts and save them as scopes
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {scopes.map((scope) => (
                    <div
                      key={scope.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedScope?.id === scope.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                      }`}
                      onClick={() => handleEdit(scope)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{scope.name}</h3>
                          {scope.description && (
                            <p className="mt-1 text-sm text-gray-600">{scope.description}</p>
                          )}
                          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                            <span>{scope.deliverables.length} deliverable{scope.deliverables.length !== 1 ? 's' : ''}</span>
                            <span>•</span>
                            <span>Updated {new Date(scope.updatedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(scope);
                          }}
                          className="ml-4 p-2 text-gray-400 hover:text-red-600"
                          title="Delete scope"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scope Editor */}
        <div>
          {isEditing && selectedScope ? (
            <div className="bg-white shadow-sm rounded-lg">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Edit Scope</h2>
              </div>

              <div className="p-4 space-y-6">
                {/* Scope Name */}
                <div>
                  <label htmlFor="scope-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Scope Name *
                  </label>
                  <input
                    type="text"
                    id="scope-name"
                    value={scopeName}
                    onChange={(e) => setScopeName(e.target.value)}
                    placeholder="e.g., SharePoint Intranet Implementation"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={saving}
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="scope-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="scope-description"
                    value={scopeDescription}
                    onChange={(e) => setScopeDescription(e.target.value)}
                    rows={2}
                    placeholder="Brief description of this scope..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={saving}
                  />
                </div>

                {/* Deliverables */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Deliverables</h3>
                    <button
                      type="button"
                      onClick={addDeliverable}
                      disabled={saving}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100 disabled:opacity-50"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      Add
                    </button>
                  </div>

                  <div className="space-y-3">
                    {deliverables.length === 0 ? (
                      <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <p className="text-sm text-gray-500">No deliverables yet</p>
                      </div>
                    ) : (
                      deliverables.map((deliverable, index) => (
                        <div key={deliverable.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-xs font-medium text-gray-700">Deliverable {index + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeDeliverable(deliverable.id)}
                              disabled={saving}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={deliverable.title}
                              onChange={(e) => updateDeliverable(deliverable.id, 'title', e.target.value)}
                              placeholder="Title"
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              disabled={saving}
                            />
                            <textarea
                              value={deliverable.description}
                              onChange={(e) => updateDeliverable(deliverable.id, 'description', e.target.value)}
                              placeholder="Description"
                              rows={2}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                              disabled={saving}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || !scopeName.trim() || deliverables.length === 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg p-8 text-center text-gray-500">
              <BookmarkIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p>Select a scope to edit</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog />
    </>
  );
}

